import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
    this.animateHeaderCountersOnLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (event.detail.data?.didError) {
      this.syncCartBubbleFromCart();
      return;
    }

    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Syncs the cart bubble with Shopify's actual cart state after add-to-cart errors.
   * Inventory-limit responses can be partial or rejected, so the requested quantity is not reliable.
   */
  syncCartBubbleFromCart = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      this.renderCartBubble(Number(cart.item_count) || 0, false);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    const hasCart = itemCount > 0;
    this.classList.toggle('header-actions__cart-icon--has-cart', hasCart && this.classList.contains('header-actions__cart-icon'));
    this.classList.toggle('bottom-nav-cart-icon--has-cart', hasCart && this.classList.contains('bottom-nav-cart-icon'));

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubble);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Animates visible desktop header counters when they are rendered on page load.
   */
  animateHeaderCountersOnLoad = async () => {
    if (!this.classList.contains('header-actions__cart-icon')) return;
    if (!window.matchMedia('(min-width: 990px)').matches) return;

    const headerActions = this.closest('header-actions');
    const wishlistCounter = headerActions?.querySelector('.wishlist-count-bubble');
    const hasRestoredWishlistCounter = document.documentElement.dataset.headerWishlistCount !== undefined;

    const counters = [
      this.refs.cartBubble.classList.contains('visually-hidden') ? null : this.refs.cartBubble,
      wishlistCounter instanceof HTMLElement &&
      (hasRestoredWishlistCounter || wishlistCounter.classList.contains('is-visible'))
        ? wishlistCounter
        : null,
    ].filter((counter) => counter instanceof HTMLElement);

    if (counters.length === 0) return;

    for (let i = 0; i < 2; i++) {
      counters.forEach((counter) => counter.classList.add('header-counter--animating'));
      await onAnimationEnd(counters);
      counters.forEach((counter) => counter.classList.remove('header-counter--animating'));

      // Force the next class addition to start a fresh animation.
      counters.forEach((counter) => void counter.offsetWidth);
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
